import React, { useState } from "react";

const UserRegistrationForm = ({ userInfo, setUserInfo, onStart, testTitle, testDescription, buttonText, additionalInfo }) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      // Format phone number as user types
      let formattedValue = value;
      
      // Remove all non-digits
      const digits = value.replace(/\D/g, '');
      
      if (digits.length > 0) {
        // Format as +7 (XXX) XXX-XX-XX
        if (digits.length <= 1) {
          formattedValue = '+7';
        } else if (digits.length <= 4) {
          formattedValue = `+7 (${digits.slice(1, 4)}`;
        } else if (digits.length <= 7) {
          formattedValue = `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}`;
        } else if (digits.length <= 9) {
          formattedValue = `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}`;
        } else {
          formattedValue = `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
        }
      }
      
      setUserInfo({
        ...userInfo,
        [field]: formattedValue
      });
    } else {
      setUserInfo({
        ...userInfo,
        [field]: value
      });
    }
  };

  const isFormValid = () => {
    // Phone number validation: must be exactly 11 digits starting with +7
    const phoneRegex = /^\+7\s?\(?[0-9]{3}\)?\s?[0-9]{3}-?[0-9]{2}-?[0-9]{2}$/;
    const cleanPhone = userInfo.phone.replace(/\D/g, ''); // Remove all non-digits
    const isValidPhone = cleanPhone.length === 11 && cleanPhone.startsWith('7');
    
    return userInfo.full_name.trim() && 
           userInfo.email.trim() && 
           userInfo.phone.trim() &&
           /\S+@\S+\.\S+/.test(userInfo.email) && // Basic email validation
           isValidPhone && // Phone validation
           agreedToTerms; // Require agreement checkbox
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          {testTitle}
        </h1>
        
        {testDescription}

        {additionalInfo}

        <div className="mb-6 p-4 bg-blue-50 rounded-md">
          <h3 className="font-semibold text-blue-800 mb-2">Для начала тестирования:</h3>
          <p className="text-blue-700 text-sm">
            Пожалуйста, заполните все поля ниже. Эта информация поможет нам связаться с вами 
            для обсуждения результатов и предоставления персональных рекомендаций.
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Полное имя <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userInfo.full_name}
              onChange={(e) => handleInputChange('full_name', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите ваше полное имя"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={userInfo.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите ваш email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Номер телефона <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={userInfo.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 ${
                userInfo.phone.trim() && 
                (userInfo.phone.replace(/\D/g, '').length !== 11 || !userInfo.phone.replace(/\D/g, '').startsWith('7'))
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="+7 (999) 123-45-67"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Формат: +7 (999) 123-45-67 (11 цифр)
            </p>
          </div>
        </div>

        {/* Agreement Checkbox */}
        <div className="mb-4">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mr-3 mt-1 text-blue-600 focus:ring-blue-500"
              required
            />
            <span className="text-gray-700 text-sm">
              <strong>Я согласен(-на)</strong> с обработкой персональных данных и получением результатов тестирования. 
              <a
                className="js-scroll px-2"
                href="https://docs.google.com/document/d/1gw72sTBQISouajjldqSrwy7KVwOi2jZOUNeYsCDeMR8/edit?usp=sharing"
                role="button"
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong> Подробнее.</strong>
              </a>
            </span>
          </label>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-md">
          <p className="text-gray-600 text-sm">
            <strong>Конфиденциальность:</strong> Ваши данные будут использованы только для связи 
            с вами по поводу результатов тестирования и не будут переданы третьим лицам.
          </p>
        </div>

        <button
          onClick={onStart}
          disabled={!isFormValid()}
          className={`w-full py-3 rounded-md font-medium transition-colors ${
            isFormValid()
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {buttonText}
        </button>

        {!isFormValid() && (
          <p className="text-red-600 text-sm mt-2 text-center">
            {!agreedToTerms 
              ? "Пожалуйста, согласитесь с условиями и заполните все обязательные поля"
              : !userInfo.full_name.trim()
              ? "Пожалуйста, введите ваше полное имя"
              : !userInfo.email.trim() || !/\S+@\S+\.\S+/.test(userInfo.email)
              ? "Пожалуйста, введите корректный email адрес"
              : !userInfo.phone.trim() || userInfo.phone.replace(/\D/g, '').length !== 11 || !userInfo.phone.replace(/\D/g, '').startsWith('7')
              ? "Пожалуйста, введите корректный номер телефона в формате +7 (999) 123-45-67"
              : "Пожалуйста, заполните все обязательные поля"
            }
          </p>
        )}
      </div>
    </div>
  );
};

export default UserRegistrationForm;
